import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, Palette, Star, Building, Users, MapPin, Globe, ArrowRight } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 类型配置
const TYPE_CONFIG = {
  artists: {
    title: '艺术家',
    titleEn: 'Artists',
    description: '发现才华横溢的艺术创作者',
    icon: Palette,
    userType: 'artist',
    gradient: 'from-gray-900 to-black',
    bgImage: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=1920&q=80'
  },
  collectors: {
    title: '收藏家',
    titleEn: 'Collectors',
    description: '探索私人艺术珍藏',
    icon: Star,
    userType: 'collector',
    gradient: 'from-amber-900 to-amber-950',
    bgImage: 'https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?w=1920&q=80'
  },
  museums: {
    title: '美术馆',
    titleEn: 'Museums & Galleries',
    description: '走进艺术殿堂',
    icon: Building,
    userType: 'museum',
    gradient: 'from-indigo-900 to-indigo-950',
    bgImage: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=1920&q=80'
  }
};

const ArtCommunityListPage = () => {
  const { type } = useParams(); // artists, collectors, museums
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const config = TYPE_CONFIG[type] || TYPE_CONFIG.artists;
  const Icon = config.icon;

  useEffect(() => {
    fetchMembers();
  }, [type]);

  const fetchMembers = async () => {
    setIsLoading(true);
    try {
      // 获取该类型的用户
      const res = await axios.get(`${API}/users/by-type/${config.userType}?limit=50`);
      setMembers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 过滤搜索结果
  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true;
    const name = (member.name || '').toLowerCase();
    const bio = (member.bio || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || bio.includes(searchQuery.toLowerCase());
  });

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid={`art-community-${type}`}>
        {/* Hero Section */}
        <section className="relative h-[50vh] flex items-end">
          <div className="absolute inset-0">
            <img loading="lazy"
              src={config.bgImage}
              alt={config.title}
              className="w-full h-full object-cover"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${config.gradient} opacity-80`} />
          </div>

          {/* 返回按钮 */}
          <Button
            variant="ghost"
            className="absolute top-20 left-6 text-white hover:bg-white/20"
            onClick={() => navigate('/artcanvas')}
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> 返回 ArtCanvas
          </Button>
          
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-white/60 text-sm">{config.titleEn}</p>
                <h1 className="text-4xl md:text-5xl font-light text-white">{config.title}</h1>
              </div>
            </div>
            <p className="text-lg text-white/70">{config.description}</p>
          </div>
        </section>

        {/* 搜索栏 */}
        <section className="py-8 px-6 border-b border-amber-100 sticky top-16 bg-amber-50 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`搜索${config.title}...`}
                  className="pl-10 rounded-full"
                />
              </div>
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                共 {filteredMembers.length} 位{config.title}
              </Badge>
            </div>
          </div>
        </section>

        {/* 成员列表 */}
        <section className="py-12 px-6">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="text-center py-20">
                <div className="w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-20">
                <Icon className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                <p className="text-amber-600 mb-2">暂无{config.title}</p>
                <p className="text-amber-500 text-sm">成为第一位入驻的{config.title}吧</p>
                <Button className="mt-6" onClick={() => navigate('/creator-center')}>
                  申请入驻
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="group bg-white rounded-2xl border hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/profile/${member.id}`)}
                  >
                    {/* 头图区域 */}
                    <div className="relative h-32 bg-gradient-to-br from-amber-100 to-amber-200">
                      {member.artworks?.[0]?.image_url && (
                        <img loading="lazy"
                          src={getImageUrl(member.artworks[0].image_url)}
                          alt=""
                          className="w-full h-full object-cover opacity-60"
                        />
                      )}
                      <div className="absolute -bottom-10 left-6">
                        <div className="w-20 h-20 rounded-full border-4 border-white bg-amber-100 overflow-hidden">
                          {member.avatar ? (
                            <img loading="lazy"
                              src={getImageUrl(member.avatar)}
                              alt={member.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-8 h-8 text-amber-400" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 信息区域 */}
                    <div className="pt-16 pb-6 px-6">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-medium text-amber-900">
                            {member.name}
                          </h3>
                          {member.institution_name && (
                            <p className="text-sm text-amber-600">{member.institution_name}</p>
                          )}
                        </div>
                        {member.creator_verified && (
                          <Badge className="bg-blue-100 text-blue-700">已认证</Badge>
                        )}
                      </div>

                      <p className="text-amber-700 text-sm line-clamp-2 mb-4 min-h-[40px]">
                        {member.bio || '暂无简介'}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-amber-500 mb-4">
                        {member.institution_location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {member.institution_location}
                          </span>
                        )}
                        {member.institution_website && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-4 h-4" />
                            官网
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-amber-600">
                          <span>{member.artworks_count || 0} 作品</span>
                          <span>{member.followers_count || 0} 粉丝</span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-amber-400 group-hover:text-amber-700 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default ArtCommunityListPage;
